import { useId, useMemo } from "react";
import styled from "styled-components";
import { ArrowTrendingUpIcon } from "@heroicons/react/24/outline";
import { Amount, Icon } from "@sablier/v2-components/molecules";
import { Input } from "@sablier/v2-components/organisms";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Characteristic } from "~/client/components/molecules";
import { useModalSimulator, useToken } from "~/client/hooks";
import { useSimulator } from "./logic";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    grid-column: span 1;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    height: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1);
    border: 2px solid ${(props) => props.theme.colors.border};
    border-radius: 8px;
    background-color: ${(props) => props.theme.colors.transparent};
  }
`;

const Description = styled.div`
  & > p {
    ${(props) => props.theme.styles.textParagraph}
    & {
      color: ${(props) => props.theme.colors.gray300};
      line-height: 20pt;
    }
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    *[data-component="box"] {
      --background: ${(props) => props.theme.colors.dark050};
    }
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme.colors.border};
`;

const Result = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1);
    border: 2px solid ${(props) => props.theme.colors.border};
    border-radius: 6px;
    & > *:nth-child(3) {
      width: auto;
    }
    & > p {
      ${(props) => props.theme.styles.textParagraph}
      & {
        color: ${(props) => props.theme.colors.gray300};
        text-align: center;
      }
    }
  }
`;

const Value = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    flex: 1;
    justify-content: center;
    width: 100%;
  }
`;

const Action = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    --size: 40px;
    --thickness: 1.6px;
    flex: 1;
    justify-content: center;
    width: 100%;
    color: ${(props) => props.theme.colors.dark400};
    cursor: pointer;
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxXL} {
    ${Result} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    }
  }
`;

function Form() {
  const { data } = useModalSimulator();
  const stream = useMemo(() => data.stream, [data]);
  const { date, amount, onClick, placeholder } = useSimulator(stream);
  const id = useId();
  const token = useToken({ token: data.stream?.token });
  const { t } = useT();

  return (
    <Wrapper>
      <Description>
        <p>
          {t("modalCards.simulate.description", {
            start: _.toDuration(stream?.startTime, "date")[0],
            end: _.toDuration(stream?.endTime, "date")[0],
          })}
        </p>
      </Description>
      <Content>
        <Input.Moment
          id={id}
          isBordered
          onClick={onClick}
          placeholder={placeholder}
          value={date}
        />
        <Divider />
        <Result>
          {!_.isNilOrEmptyString(date) && !_.isNilOrEmptyString(amount) ? (
            <>
              <p>
                {t("modalCards.simulate.summary", {
                  date: _.toDuration(date, "date")[0],
                })}
              </p>
              <Value>
                <Amount value={amount} size={[42, 24]} token={token} />
              </Value>
              <Characteristic.Simulated
                amount={stream?.depositAmount.humanized.toString()}
                token={token}
              />
            </>
          ) : (
            <Action onClick={onClick}>
              <Icon value={ArrowTrendingUpIcon} />
            </Action>
          )}
        </Result>
      </Content>
    </Wrapper>
  );
}

export default Form;
